import { X, User, Mail, Phone, Briefcase, MapPin, Tag } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const ContactModal = ({ isOpen, onClose, onSave, contact = null }) => {
  const modalRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    work: "",
    address: "",
    origin: "MANUAL",
  });

  const [errors, setErrors] = useState({});

  const originOptions = [
    { value: "MANUAL", label: "Manual" },
    { value: "WEBSITE", label: "Website" },
    { value: "GOOGLE_ADS", label: "Google Ads" },
    { value: "INDICATION", label: "Indicação" },
    { value: "EVENT", label: "Evento" },
  ];

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
    }
    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      onClose();
    }
  };

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || "",
        phoneNumber: contact.phoneNumber || "",
        email: contact.email || "",
        work: contact.work || "",
        address: contact.address || "",
        origin: contact.origin || "MANUAL",
      });
    } else {
      setFormData({
        name: "",
        phoneNumber: "",
        email: "",
        work: "",
        address: "",
        origin: "MANUAL",
      });
    }
    setErrors({});
  }, [contact, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Nome é obrigatório";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Telefone é obrigatório";

    if (formData.email && !formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.email = "Email inválido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const formattedPhoneNumber = `55${formData.phoneNumber.trim()}`;

    if (validateForm()) {
      onSave({
        ...formData,
        phoneNumber: formattedPhoneNumber,
        id: contact?.id,
      });
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
        >
          <motion.div
            ref={modalRef}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-2xl rounded-lg bg-white shadow-xl dark:bg-gray-900"
          >
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <h2 className="flex items-center text-xl font-semibold text-gray-900 dark:text-gray-100">
                <User className="mr-2 h-5 w-5" />
                {contact ? "Editar Contato" : "Novo Contato"}
              </h2>
              <button
                onClick={onClose}
                className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-5">
                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <User className="mr-2 h-4 w-4" />
                    Nome *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full rounded-lg border ${
                      errors.name
                        ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                    } bg-white px-4 py-2 text-gray-900 transition-colors focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                    placeholder="Digite o nome completo"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Phone className="mr-2 h-4 w-4" />
                      Telefone
                    </label>
                    <input
                      type="text"
                      name="phoneNumber"
                      value={formData.phoneNumber.replace(/\D/g, "")}
                      onChange={handleChange}
                      className={`w-full rounded-lg border ${
                        errors.phoneNumber
                          ? "border-red-500"
                          : "border-gray-300"
                      } bg-white px-4 py-2 text-gray-900 transition-colors focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                      placeholder="(00) 00000-0000"
                    />
                    {errors.phoneNumber && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.phoneNumber}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                      <Mail className="mr-2 h-4 w-4" />
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full rounded-lg border ${
                        errors.email ? "border-red-500" : "border-gray-300"
                      } bg-white px-4 py-2 text-gray-900 transition-colors focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
                      placeholder="email@exemplo.com"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.email}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Briefcase className="mr-2 h-4 w-4" />
                    Cargo
                  </label>
                  <input
                    type="text"
                    name="work"
                    value={formData.work}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 transition-colors focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    placeholder="Ex: Gerente de Vendas"
                  />
                </div>

                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <MapPin className="mr-2 h-4 w-4" />
                    Endereço
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 transition-colors focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    placeholder="Rua, Número, Cidade - Estado"
                  />
                </div>

                <div>
                  <label className="mb-2 flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Tag className="mr-2 h-4 w-4" />
                    Origem *
                  </label>
                  <select
                    name="origin"
                    value={formData.origin}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 transition-colors focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    {originOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3 border-t border-gray-200 pt-6 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-600"
                >
                  {contact ? "Salvar Alterações" : "Criar Contato"}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
